/**
 * TenantThemeContext — fetches the active tenant's UI theme on mount and
 * applies every token as a `--vl-*` CSS variable on :root, so any element
 * styled with `var(--vl-btn-primary-from)` etc. instantly inherits the
 * per-tenant look. Also injects `theme.custom_css` verbatim into <head>.
 *
 * Resolution order:
 *   1. `X-Tenant-Slug` derived from the URL (e.g. /groupa/app → "groupa")
 *   2. Platform default (Mystic Reels palette returned by backend)
 */
import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import api from '../services/api';

const TenantThemeContext = createContext(null);
const PLATFORM_DOMAIN = (process.env.REACT_APP_PLATFORM_DOMAIN || '').toLowerCase();
const RESERVED_SEGMENTS = ['auth', 'app', 'admin', 'api', 'staff', 'preview', 'staff-portal', 'finance', 'deposit-return'];

const STARLIGHT_SLUGS = new Set(['starlight', 'startlight', 'mystical']);
const STARLIGHT_NAVY_THEME = {
  bg: 'linear-gradient(90deg, #152235 0%, #17283A 34%, #1D3349 67%, #223B54 100%)',
  landing_bg: 'linear-gradient(90deg, #152235 0%, #17283A 34%, #1D3349 67%, #223B54 100%)',
  surface: 'rgba(21, 34, 53, 0.92)',
  surface_alt: 'rgba(29, 51, 73, 0.88)',
  header_from: '#152235',
  header_to: '#223B54',
};

const isStarlightSlug = (slug) => STARLIGHT_SLUGS.has((slug || '').toLowerCase());

const withTenantOverrides = (slug, theme) => {
  const base = theme || {};
  if (!isStarlightSlug(slug)) return base;
  return { ...base, ...STARLIGHT_NAVY_THEME };
};

const tenantThemeCacheKey = (slug) => `vl_tenant_theme:${String(slug || '').toLowerCase()}`;

const readCachedTheme = (slug) => {
  if (!slug) return null;
  try {
    const raw = localStorage.getItem(tenantThemeCacheKey(slug));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

const writeCachedTheme = (slug, theme) => {
  if (!slug || !theme || typeof theme !== 'object') return;
  try {
    localStorage.setItem(tenantThemeCacheKey(slug), JSON.stringify(theme));
  } catch {
    // Ignore storage failures; live theme still applies for this page.
  }
};

const getSlugFromPath = () => {
  const path = window.location.pathname;
  const match = path.match(/^\/([a-z0-9-]+)(\/|$)/i);
  if (match && !RESERVED_SEGMENTS.includes(match[1])) return match[1];
  const host = window.location.hostname || '';
  const parts = host.split('.').filter(Boolean);
  if (PLATFORM_DOMAIN && parts.length >= 3 && parts.slice(-2).join('.') === PLATFORM_DOMAIN) {
    const subdomain = parts[0].toLowerCase();
    if (!['www', 'api', 'admin'].includes(subdomain)) return subdomain;
  }
  return null;
};

// Map a flat theme dict { btn_primary_from: "#22FF88", ... } to CSS
// custom-property pairs { "--vl-btn-primary-from": "#22FF88", ... }.
// All non-string values are skipped so we don't render `[object Object]`.
const themeToCssVars = (theme) => {
  const out = {};
  if (!theme || typeof theme !== 'object') return out;
  for (const [key, val] of Object.entries(theme)) {
    if (typeof val !== 'string' || !val.length) continue;
    if (key === 'custom_css') continue;          // injected separately
    if (key === 'logo_url' || key === 'intro_logo_url' || key === 'brand_name' || key === 'tagline') continue;
    const cssKey = '--vl-' + key.replace(/_/g, '-');
    out[cssKey] = val;
  }
  return out;
};

export function TenantThemeProvider({ children }) {
  const initialSlug = getSlugFromPath();
  const cachedInitialTheme = readCachedTheme(initialSlug);
  const [theme, setTheme] = useState(() => withTenantOverrides(initialSlug, cachedInitialTheme || {}));
  const [loading, setLoading] = useState(Boolean(initialSlug));
  const [ready, setReady] = useState(Boolean(!initialSlug));
  const [tenantSlug, setTenantSlug] = useState(initialSlug);

  const applyTheme = useCallback((t) => {
    if (!t || typeof t !== 'object') return;
    const root = document.documentElement;
    const vars = themeToCssVars(t);
    for (const [k, v] of Object.entries(vars)) {
      root.style.setProperty(k, v);
    }
    // Inject custom CSS verbatim — replace any previous block so admins
    // can iterate without page reload.
    const styleId = 'vl-tenant-theme-custom-css';
    let styleEl = document.getElementById(styleId);
    const cssBody = (t.custom_css || '').toString().trim();
    if (cssBody) {
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = styleId;
        document.head.appendChild(styleEl);
      }
      styleEl.textContent = cssBody;
    } else if (styleEl) {
      styleEl.remove();
    }
  }, []);

  const fetchTheme = useCallback(async (slug) => {
    setLoading(true);
    try {
      document.documentElement.dataset.tenantSlug = slug || '';
      const res = await api.get('/api/tenant/theme', {
        headers: slug ? { 'X-Tenant-Slug': slug } : {},
      });
      const t = withTenantOverrides(slug, res.data?.theme || {});
      setTheme(t);
      applyTheme(t);
      writeCachedTheme(slug, t);
    } catch (e) {
      // Fail silent — page just renders with the Tailwind hard-coded look.
      console.warn('[TenantTheme] fetch failed', e?.message);
      const fallback = withTenantOverrides(slug, readCachedTheme(slug) || {});
      setTheme(fallback);
      applyTheme(fallback);
    } finally {
      setLoading(false);
      setReady(true);
    }
  }, [applyTheme]);

  useEffect(() => {
    if (tenantSlug && !readCachedTheme(tenantSlug)) {
      setReady(false);
    }
    fetchTheme(tenantSlug);
  }, [fetchTheme, tenantSlug]);

  // Re-fetch on URL change (single-page navigation between /groupa/app and /app)
  useEffect(() => {
    const handler = () => {
      const next = getSlugFromPath();
      if (next !== tenantSlug) {
        const cached = readCachedTheme(next);
        const nextTheme = withTenantOverrides(next, cached || {});
        setTheme(nextTheme);
        applyTheme(nextTheme);
        setReady(Boolean(!next || cached));
        setTenantSlug(next);
      }
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, [applyTheme, tenantSlug]);

  const value = useMemo(() => ({
    theme: theme || {},
    loading,
    tenantSlug,
    refresh: () => fetchTheme(tenantSlug),
    /** Used by the admin live-preview pane to push a draft theme onto :root
     * without persisting. Pass null/empty obj to revert to the saved theme. */
    previewTheme: (draft) => applyTheme(draft || theme || {}),
  }), [theme, loading, tenantSlug, fetchTheme, applyTheme]);

  if (tenantSlug && !ready) {
    const bootTheme = withTenantOverrides(tenantSlug, {});
    return (
      <TenantThemeContext.Provider value={value}>
        <div
          className="min-h-screen flex items-center justify-center"
          style={{ background: bootTheme.bg || '#0B0E14' }}
          data-testid="tenant-theme-boot"
        >
          <div className="spinner" />
        </div>
      </TenantThemeContext.Provider>
    );
  }

  return (
    <TenantThemeContext.Provider value={value}>
      {children}
    </TenantThemeContext.Provider>
  );
}

export function useTenantTheme() {
  const ctx = useContext(TenantThemeContext);
  if (!ctx) {
    // Return a safe default so components in the tree can render without
    // the provider (e.g. landing page) — hooks must always be callable.
    return { theme: {}, loading: false, tenantSlug: null, refresh: () => {}, previewTheme: () => {} };
  }
  return ctx;
}

export { themeToCssVars };
